<template>
  <div class="spr-positive-integer-input pb-2">
    <label v-if="title" class="cap-label-small">{{ $t(title) }}</label>

    <input
      v-model="inputValue"
      type="text"
      inputmode="numeric"
      pattern="[0-9]*"
      class="smalltext form-control"
      :disabled="disabled || isSending"
      @input="handleInput"
    />
    <div class="pn-buttons">
      <button type="button" class="smalltext" :disabled="!canSubmit" @click="saveChanges">
        <font-awesome-icon :icon="['far', 'save']" aria-hidden="true" />
        {{ $t('profile.save') }}
      </button>
      <button type="button" class="smalltext" :disabled="!canRevert" @click="revertChanges">
        <font-awesome-icon icon="times" aria-hidden="true" />
        {{ $t('profile.cancel') }}
      </button>
    </div>
    <div v-if="hasValidationError" class="validationerror">
      {{ validationMessage }}
    </div>
    <div v-if="isPostError" class="posterror">
      {{ $t('profile.saveFailed') }}
    </div>
    <div v-if="isSending" class="loading">
      <loading class="spinner" />
    </div>
  </div>
</template>

<script>
  import loading from './SPRLoading.vue'

  export default {
    name: 'SprPositiveIntegerInput',
    components: { loading },
    props: {
      value: {
        type: Number,
        default: 0,
      },
      title: {
        type: String,
        default: '',
      },
      disabled: {
        type: Boolean,
        default: false,
      },
      min: {
        type: Number,
        default: 1,
      },
      saveHandler: {
        type: Function,
        required: true,
      },
    },
    data() {
      return {
        originalValue: 0,
        inputValue: '',
        isSending: false,
        isPostError: false,
      }
    },
    computed: {
      isDirty: function () {
        return this.inputValue !== String(this.originalValue)
      },
      parsedInput: function () {
        if (!this.inputValue || !/^\d+$/.test(this.inputValue)) {
          return null
        }

        const parsed = Number(this.inputValue)
        if (!Number.isSafeInteger(parsed) || parsed < this.min) {
          return null
        }

        return parsed
      },
      hasValidationError: function () {
        return this.isDirty && this.parsedInput === null
      },
      canSubmit: function () {
        return (
          !this.disabled &&
          !this.isSending &&
          !this.hasValidationError &&
          this.parsedInput !== null &&
          this.parsedInput !== this.originalValue
        )
      },
      canRevert: function () {
        return !this.disabled && !this.isSending && this.inputValue !== String(this.originalValue)
      },
      validationMessage: function () {
        return `Please enter an integer >= ${this.min}.`
      },
    },
    watch: {
      value: {
        immediate: true,
        handler: function () {
          const safeValue = Number.isSafeInteger(this.value) ? this.value : 0
          this.originalValue = safeValue
          this.inputValue = String(safeValue)
        },
      },
    },
    methods: {
      handleInput() {
        this.isPostError = false
        this.inputValue = String(this.inputValue || '').replace(/[^\d]/g, '')
      },
      saveChanges() {
        if (!this.canSubmit) {
          return
        }

        if (typeof this.saveHandler !== 'function') {
          this.isPostError = true
          return
        }

        this.isPostError = false
        this.isSending = true
        const valueToSave = this.parsedInput

        Promise.resolve()
          .then(() => this.saveHandler(valueToSave))
          .then(() => {
            this.isSending = false
            this.originalValue = Number(valueToSave)
            this.inputValue = String(valueToSave)
            this.$emit('saved', valueToSave)
          })
          .catch(() => {
            this.isSending = false
            this.isPostError = true
            console.error('Error saving integer value')
          })
      },
      revertChanges() {
        this.isPostError = false
        this.inputValue = String(this.originalValue)
      },
    },
  }
</script>

<style lang="scss" scoped>
  @import '../assets/scss/_variables.scss';

  .spr-positive-integer-input {
    margin-top: 8px;
    border-radius: 20px;
    border: solid 2px $brand-red;
    background-color: $brand-white;
    position: relative;

    label {
      color: $brand-red;
      padding: 0 20px 10px;
      display: block;
    }

    input {
      border: none;
      box-shadow: none;
      background: $brand-white;
    }
    .cap-label-small {
      padding-top: 5px;
      padding-bottom: 0px;
      margin-bottom: 0px;
      text-transform: uppercase;
      font-family: 'Signa-Bold', sans-serif;
      font-size: 14px;
      line-height: 19px;
      letter-spacing: 0.3px;
    }
    .pn-buttons {
      margin-top: 0px;
      border-top: 1px solid $brand-grey3;
      padding: 0px 10px 0;
      text-align: right;

      button {
        border: none;
        background: none;
        color: $brand-red;
        cursor: pointer;

        &:disabled {
          color: $brand-grey4 !important;
          cursor: not-allowed;
        }
      }
    }

    .validationerror,
    .posterror {
      text-align: center;
      color: $brand-red;
      font-size: 13px;
    }

    .loading {
      position: absolute;
      display: flex;
      top: 0;
      left: 0;
      height: 100%;
      width: 100%;
      background-color: rgba(255, 255, 255, 0.8);

      .spinner {
        padding-top: 26px;
        margin: auto;
        color: $brand-red;
      }
    }
  }
</style>
